import { useState, useEffect, useCallback } from "react";
import { rbacApi } from "../services/api";

// Fallback baseline in case network fails before first sync
const DEFAULT_PERMISSIONS = {
  1: ["*"], // Super Admin
  2: [
    "DASHBOARD:VIEW", "DEVICES:VIEW", "DEVICES:CREATE", "DEVICES:EDIT", "DEVICES:DELETE",
    "FACILITIES:VIEW", "FACILITIES:CREATE", "FACILITIES:EDIT", "FACILITIES:DELETE",
    "ZONES:VIEW", "ZONES:CREATE", "ZONES:EDIT", "ZONES:DELETE",
    "BOXES:VIEW", "BOXES:EDIT",
    "HARDWARE:UNLOCK_SINGLE", "HARDWARE:BULK_OPEN_VACANT", "HARDWARE:RESET",
    "ORDERS:VIEW", "ORDERS:CREATE", "ORDERS:EDIT", "ORDERS:DELETE", "ORDERS:REASSIGN_COURIER", "ORDERS:RESEND_PIN",
    "LOGISTICS:VIEW", "LOGISTICS:CREATE", "LOGISTICS:EDIT", "LOGISTICS:DELETE",
    "PAYMENT_RECORDS:VIEW", "PAYMENT_TARIFFS:VIEW",
    "COURIERS:VIEW", "COURIERS:CREATE", "COURIERS:EDIT", "COURIERS:BIND_DEVICE",
    "CUSTOMERS:VIEW", "CUSTOMERS:CREATE", "CUSTOMERS:EDIT",
    "CLIENT_SETTINGS:VIEW", "CLIENT_SETTINGS:EDIT",
    "CLIENT_VERSION:VIEW", "CLIENT_VERSION:EDIT",
    "NOTIFICATIONS:VIEW", "NOTIFICATIONS:EDIT_MATRIX",
    "TEMPLATES:VIEW", "TEMPLATES:EDIT",
    "AUDIT_LOGS:VIEW", "AUDIT_LOGS:EXPORT",
    "ACCESS_CONTROL:VIEW"
  ],
  3: [
    "DASHBOARD:VIEW",
    "DEVICES:VIEW",
    "FACILITIES:VIEW",
    "ZONES:VIEW",
    "BOXES:VIEW",
    "ORDERS:VIEW", "ORDERS:CREATE", "ORDERS:RESEND_PIN",
    "LOGISTICS:VIEW",
    "COURIERS:VIEW",
    "CUSTOMERS:VIEW",
    "NOTIFICATIONS:VIEW",
    "TEMPLATES:VIEW"
  ]
};

let cachedPermissionsState = {
  roleId: null,
  roleName: "Super Admin",
  permissions: new Set(["*"]),
  revision: 1,
  loaded: false
};

const listeners = new Set();

function notifyListeners() {
  listeners.forEach((fn) => {
    try { fn(); } catch (_) {}
  });
}

export function syncMyPermissions() {
  return rbacApi.getMyPermissions()
    .then((res) => {
      const data = res.data?.data;
      if (data) {
        const perms = new Set(data.permissions || []);
        if (data.isSuperAdmin) perms.add("*");
        cachedPermissionsState = {
          roleId: data.roleId,
          roleName: data.roleName,
          permissions: perms,
          revision: data.revision || 1,
          loaded: true
        };
        try {
          localStorage.setItem("mybox_rbac_cached", JSON.stringify({
            roleId: data.roleId,
            roleName: data.roleName,
            permissions: Array.from(perms),
            revision: data.revision || 1
          }));
        } catch (_) {}
        notifyListeners();
      }
      return cachedPermissionsState;
    })
    .catch(() => {
      // Offline fallback: try reading from localStorage
      try {
        const raw = localStorage.getItem("mybox_rbac_cached");
        if (raw) {
          const parsed = JSON.parse(raw);
          cachedPermissionsState = {
            roleId: parsed.roleId,
            roleName: parsed.roleName,
            permissions: new Set(parsed.permissions),
            revision: parsed.revision,
            loaded: true
          };
          notifyListeners();
        }
      } catch (_) {}
    });
}

// Auto-trigger sync on load if user is logged in
if (typeof window !== "undefined") {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  if (token) {
    syncMyPermissions();
  }
}

export default function useRBAC() {
  const getInitialRole = () => {
    try {
      const userInfoStr = localStorage.getItem("userInfo") || localStorage.getItem("user");
      if (userInfoStr) {
        const userInfo = JSON.parse(userInfoStr);
        if (userInfo.name === "admin" || userInfo.username === "admin" || userInfo.account === "admin") {
          return "Super Admin";
        }
        if (userInfo.role !== undefined && userInfo.role !== null) {
          const r = String(userInfo.role).toLowerCase();
          if (r === "super admin" || r === "superadmin" || r === "1") return "Super Admin";
          if (r === "admin" || r === "2") return "Admin";
          if (r === "manager" || r === "3") return "Manager";
        }
        if (userInfo.roleName) {
          const rn = String(userInfo.roleName).toLowerCase();
          if (rn.includes("super")) return "Super Admin";
          if (rn.includes("admin")) return "Admin";
          if (rn.includes("manager")) return "Manager";
        }
      }
    } catch (_) {}
    return "Super Admin";
  };

  const [role, setRole] = useState(getInitialRole);
  const [, setTick] = useState(0);

  useEffect(() => {
    const update = () => {
      setRole(getInitialRole());
      setTick((t) => t + 1);
    };
    listeners.add(update);
    window.addEventListener("storage", update);

    if (!cachedPermissionsState.loaded) {
      syncMyPermissions();
    }

    return () => {
      listeners.delete(update);
      window.removeEventListener("storage", update);
    };
  }, []);

  const isSuperAdmin = role === "Super Admin";
  const isAdmin = role === "Admin";
  const isManager = role === "Manager";

  const roleId = isSuperAdmin ? 1 : (isAdmin ? 2 : (isManager ? 3 : 3));

  const can = useCallback((permKey) => {
    if (!permKey) return true;
    if (isSuperAdmin) return true;
    if (cachedPermissionsState.permissions.has("*")) return true;
    if (cachedPermissionsState.permissions.has(permKey)) return true;

    // Fallback to static defaults if network sync hasn't populated yet
    if (!cachedPermissionsState.loaded) {
      const defaults = DEFAULT_PERMISSIONS[roleId] || [];
      return defaults.includes(permKey);
    }
    return false;
  }, [isSuperAdmin, roleId]);

  const canAny = useCallback((keys = []) => {
    return keys.some((k) => can(k));
  }, [can]);

  const canAll = useCallback((keys = []) => {
    return keys.every((k) => can(k));
  }, [can]);

  // Backward-compatible flags
  const canCreate = can('ORDERS:CREATE') || can('DEVICES:CREATE') || can('FACILITIES:CREATE') || can('LOGISTICS:CREATE') || can('ZONES:CREATE') || isSuperAdmin;
  const canDelete = can('ORDERS:DELETE') || can('DEVICES:DELETE') || can('FACILITIES:DELETE') || can('LOGISTICS:DELETE') || can('ZONES:DELETE') || isSuperAdmin;
  const canEdit = can('ORDERS:EDIT') || can('DEVICES:EDIT') || can('FACILITIES:EDIT') || can('LOGISTICS:EDIT') || can('ZONES:EDIT') || isSuperAdmin;
  const canManageHardware = can('HARDWARE:UNLOCK_SINGLE') || can('HARDWARE:RESET') || isSuperAdmin;
  const canConfigureLockers = can('BOXES:EDIT') || isSuperAdmin;
  const canManageRoles = can('ACCESS_CONTROL:MANAGE') || isSuperAdmin;
  const canViewSystemConfig = can('SYSTEM_CONFIG:VIEW') || isSuperAdmin;
  const canViewAuditLogs = can('AUDIT_LOGS:VIEW') || isSuperAdmin;
  const canManageSettings = can('SYSTEM_CONFIG:EDIT') || isSuperAdmin;

  return {
    role,
    roleId,
    isSuperAdmin,
    isAdmin,
    isManager,
    can,
    canAny,
    canAll,
    refreshPermissions: syncMyPermissions,
    revision: cachedPermissionsState.revision,
    permissions: Array.from(cachedPermissionsState.permissions),
    canCreate,
    canDelete,
    canEdit,
    canManageHardware,
    canConfigureLockers,
    canManageRoles,
    canViewSystemConfig,
    canViewAuditLogs,
    canManageSettings
  };
}
